/**
 * HOST BOOKING LIFECYCLE - Status Labels & Helper Text
 * 
 * This file is the SINGLE SOURCE OF TRUTH for all host-facing booking status labels.
 * Use these consistently across: dashboard, bookings list, booking details, and alerts.
 * 
 * Status Flow:
 * requested → approved_pending_payment → paid → prehire_in_progress → prehire_submitted 
 * → active → posthire_in_progress → posthire_submitted → cooldown_window_open → completed
 */

/**
 * Host-facing status configuration
 * Each status includes:
 * - label: Human-readable status name
 * - description: What this status means for the host
 * - helperText: What the host should do / expect next
 * - actionRequired: Whether the host needs to take action
 * - color: Tailwind color scheme
 */
export const HOST_BOOKING_STATUSES = {
  // ═══════════════════════════════════════════════════════════════
  // STAGE 1: APPROVAL & PAYMENT
  // ═══════════════════════════════════════════════════════════════
  requested: {
    label: 'Awaiting Your Approval',
    description: 'A renter has requested to book your vehicle.',
    helperText: 'Review the request and approve or decline.',
    actionRequired: true,
    actionLabel: 'Review Request',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    colorName: 'yellow',
    icon: 'Clock'
  },
  
  approved_pending_payment: {
    label: 'Awaiting Renter Payment',
    description: 'You approved this booking. Waiting for the renter to pay.',
    helperText: 'No action needed. The renter has been notified to complete payment.',
    actionRequired: false,
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    colorName: 'blue',
    icon: 'DollarSign'
  },

  approval_expired: {
    label: 'Booking Expired',
    description: 'Payment was not completed in time.',
    helperText: 'This booking has expired. Your dates have been released.',
    actionRequired: false,
    color: 'bg-slate-100 text-slate-600 border-slate-200',
    colorName: 'slate',
    icon: 'Clock'
  },

  // ═══════════════════════════════════════════════════════════════
  // STAGE 2: PRE-HIRE (HANDOVER)
  // ═══════════════════════════════════════════════════════════════
  paid: {
    label: 'Payment Received',
    description: 'The renter has paid. Ready for pre-hire inspection.',
    helperText: 'Complete the pre-hire checklist before handing over the vehicle.',
    actionRequired: true,
    actionLabel: 'Start Pre-Hire Checklist',
    color: 'bg-green-100 text-green-800 border-green-200',
    colorName: 'green',
    icon: 'CheckCircle'
  },

  prehire_in_progress: {
    label: 'Pre-Hire In Progress',
    description: 'You are completing the handover inspection.',
    helperText: 'Finish documenting the vehicle condition with photos before handover.',
    actionRequired: true,
    actionLabel: 'Continue Pre-Hire',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    colorName: 'amber',
    icon: 'Camera'
  },

  prehire_submitted: {
    label: 'Ready for Pickup',
    description: 'Pre-hire complete. Vehicle is ready for the renter.',
    helperText: 'Hand over the vehicle when the renter arrives. They will start the trip.',
    actionRequired: false,
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    colorName: 'indigo',
    icon: 'CheckCircle'
  },

  // ═══════════════════════════════════════════════════════════════
  // STAGE 3: TRIP ACTIVE
  // ═══════════════════════════════════════════════════════════════
  active: {
    label: 'Trip Active',
    description: 'The renter is currently using the vehicle.',
    helperText: 'Complete the return checklist when the vehicle is returned.',
    actionRequired: false,
    nextAction: 'Return checklist due when trip ends',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    colorName: 'emerald',
    icon: 'Car'
  },

  trip_active: {
    label: 'Trip Active',
    description: 'The renter is currently using the vehicle.',
    helperText: 'Complete the return checklist when the vehicle is returned.',
    actionRequired: false,
    nextAction: 'Return checklist due when trip ends',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    colorName: 'emerald',
    icon: 'Car'
  },

  // ═══════════════════════════════════════════════════════════════
  // STAGE 4: POST-HIRE (RETURN)
  // ═══════════════════════════════════════════════════════════════
  posthire_in_progress: {
    label: 'Return Checklist In Progress',
    description: 'You are documenting the vehicle return condition.',
    helperText: 'Complete all return photos to protect your claim eligibility.',
    actionRequired: true,
    actionLabel: 'Continue Return Checklist',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    colorName: 'orange',
    icon: 'Camera'
  },

  posthire_submitted: {
    label: 'Review Period Active',
    description: 'Return checklist complete. Review period has started.',
    helperText: 'Claims or disputes can be submitted during this period.',
    actionRequired: false,
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    colorName: 'purple',
    icon: 'Clock'
  },

  // ═══════════════════════════════════════════════════════════════
  // STAGE 5: REVIEW & COMPLETION
  // ═══════════════════════════════════════════════════════════════
  cooldown_window_open: {
    label: 'Review Period Active',
    description: 'Both parties can submit claims during this window.',
    helperText: 'If you found damage, submit a claim before the review period ends.',
    actionRequired: false,
    optionalAction: 'Submit Claim',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    colorName: 'purple',
    icon: 'Clock'
  },

  claim_submitted: {
    label: 'Claim Under Review',
    description: 'You submitted additional charges for review.',
    helperText: 'The renter has been notified. Waiting for response or admin review.',
    actionRequired: false,
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    colorName: 'orange',
    icon: 'AlertTriangle'
  },

  disputed: {
    label: 'Dispute Under Review',
    description: 'A dispute has been raised and is being reviewed.',
    helperText: 'Admin will review the evidence and make a decision.',
    actionRequired: false,
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    colorName: 'amber',
    icon: 'AlertTriangle'
  },

  completed: {
    label: 'Completed',
    description: 'This booking has been successfully completed.',
    helperText: 'All inspections done. Payout will be processed.',
    actionRequired: false,
    color: 'bg-slate-100 text-slate-800 border-slate-200',
    colorName: 'slate',
    icon: 'CheckCircle'
  },

  // ═══════════════════════════════════════════════════════════════
  // CANCELLED / DECLINED
  // ═══════════════════════════════════════════════════════════════
  cancelled: {
    label: 'Cancelled',
    description: 'This booking was cancelled.',
    helperText: 'No further action required.',
    actionRequired: false,
    color: 'bg-red-100 text-red-800 border-red-200',
    colorName: 'red',
    icon: 'XCircle'
  },

  declined: {
    label: 'Declined',
    description: 'You declined this booking request.',
    helperText: 'No further action required.',
    actionRequired: false,
    color: 'bg-red-100 text-red-800 border-red-200',
    colorName: 'red',
    icon: 'XCircle'
  },

  expired: {
    label: 'Expired',
    description: 'This booking request expired.',
    helperText: 'The renter did not complete their booking in time.',
    actionRequired: false,
    color: 'bg-slate-100 text-slate-600 border-slate-200',
    colorName: 'slate',
    icon: 'Clock'
  }
};

// Legacy status mapping
const LEGACY_STATUS_MAP = {
  'pending': 'requested',
  'accepted': 'approved_pending_payment',
  'captured': 'paid',
  'confirmed': 'paid',
  'in_progress': 'active',
  'returning': 'posthire_in_progress',
  'posthire': 'posthire_submitted'
};

/**
 * Normalize any status to a canonical status
 */
export const normalizeHostStatus = (status) => {
  if (!status) return 'requested';
  const normalized = status.toLowerCase().trim();
  return LEGACY_STATUS_MAP[normalized] || normalized;
};

/**
 * Get full status configuration for a booking
 */
export const getHostStatusConfig = (status) => {
  const normalized = normalizeHostStatus(status);
  return HOST_BOOKING_STATUSES[normalized] || HOST_BOOKING_STATUSES.requested;
};

/**
 * Get just the host-facing label
 */
export const getHostStatusLabel = (status) => {
  return getHostStatusConfig(status).label;
};

/**
 * Get helper text for a status
 */
export const getHostStatusHelperText = (status) => {
  return getHostStatusConfig(status).helperText;
};

/**
 * Check if a status requires host action
 */
export const isHostActionRequired = (status) => {
  return getHostStatusConfig(status).actionRequired === true;
};

/**
 * Get the primary action label for a status (if any)
 */
export const getHostActionLabel = (status) => {
  const config = getHostStatusConfig(status);
  return config.actionLabel || config.optionalAction || null;
};

/**
 * Get badge styling for a status
 */
export const getHostStatusBadgeClass = (status) => {
  return getHostStatusConfig(status).color;
};

/**
 * Booking list status badge configuration (simplified for list views)
 */
export const getHostListBadge = (status) => {
  const config = getHostStatusConfig(status);
  return {
    label: config.label,
    className: config.color
  };
};

/**
 * Dashboard finance badge - shows status with financial context
 */
export const getHostDashboardBadge = (booking) => {
  // Use API-provided badge if available
  if (booking.finance_badge) {
    return {
      label: booking.finance_badge.label,
      className: `bg-${booking.finance_badge.color}-100 text-${booking.finance_badge.color}-800 border-${booking.finance_badge.color}-200`
    };
  }
  
  // Check for special states
  const status = normalizeHostStatus(booking.status);
  const extraChargesStatus = booking.extra_charges_status;
  
  if (extraChargesStatus === 'disputed') {
    return { 
      label: 'Dispute Under Review', 
      className: 'bg-amber-100 text-amber-800 border-amber-200' 
    };
  }
  
  if (extraChargesStatus === 'pending' && status === 'posthire_submitted') {
    return { 
      label: 'Claim Pending Response', 
      className: 'bg-orange-100 text-orange-800 border-orange-200' 
    };
  }
  
  // Default to status config
  const config = getHostStatusConfig(status);
  return {
    label: config.label,
    className: config.color
  };
};

export default HOST_BOOKING_STATUSES;
